import * as THREE from 'three';
import {isAdjustableLeg} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    DEFAULT_SNAPPING_DISTANCE,
    KICK_PANEL_SETBACK_MM,
    SCENE_UNITS_PER_MM,
} from 'components/customer/RoomPlanner/helpers/snapping/constants';
import {
    ProductFacts,
    SnapCandidate,
    SnapContext,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';

/**
 * Where kick-length panels rest (R-12, CO-5716): under a cabinet's front, in
 * the inside corner of an L-shaped corner base, and at a right angle to the
 * end of another kick-length panel. Every function here works in the floor
 * plane on quarter-turn axes and leaves height to the caller. Products are
 * passed as their facts (snapping/context.ts), so each is measured once per
 * calculation; their boxes are shared, so anything changed is a clone.
 */

// A box's extent along a quarter-turn unit vector in the floor plane.
export const extentAlong = (bbox: THREE.Box3, direction: THREE.Vector3) => {
    const a = bbox.min.dot(direction);
    const b = bbox.max.dot(direction);

    return [Math.min(a, b), Math.max(a, b)] as const;
};

// The distance between two points in the floor plane.
export const planDistance = (a: THREE.Vector3, b: THREE.Vector3) =>
    Math.hypot(a.x - b.x, a.z - b.z);

// The way a cabinet's doors face.
export const frontOf = (cabinet: THREE.Object3D) =>
    new THREE.Vector3(
        Math.round(Math.sin(cabinet.rotation.z)),
        0,
        Math.round(Math.cos(cabinet.rotation.z))
    );

// The product's box if its origin were moved to `landing`.
export const bboxAt = (product: ProductFacts, landing: THREE.Vector3) =>
    product.bbox.clone().translate(landing.clone().sub(product.group.position));

// The origin that puts the product's box minimum at `minU` along `u` and
// `minV` along `v`.
export const landingForBBoxMin = (
    product: ProductFacts,
    u: THREE.Vector3,
    minU: number,
    v: THREE.Vector3,
    minV: number
) => {
    const {bbox} = product;

    return product.group.position
        .clone()
        .addScaledVector(u, minU - extentAlong(bbox, u)[0])
        .addScaledVector(v, minV - extentAlong(bbox, v)[0]);
};

// A kick-length panel's thickness and length in plan.
const panelExtents = ({size}: ProductFacts) => {
    return {
        thickness: Math.min(size.x, size.z),
        runsAlongX: size.x > size.z,
    };
};

/**
 * Whether a kick-length panel at `landing` would sit inside a kicker frame or
 * another kick-length panel, which already fills the toe kick there. Kicker
 * frames carry ignoreOverlap, so the drag's overlap checks don't catch them.
 * The other product's box is inset by one unit, as the drag's after-snap
 * check insets cabinets, so products meeting end to end or face to end fit.
 * @param {ProductFacts} panel The kick-length panel.
 * @param {THREE.Vector3} landing Where the panel's origin would go.
 * @param {SnapContext} ctx The calculation, for every product in the room.
 * @return {boolean} True when a floor product is in the way.
 */
export const landingMeetsFloorProduct = (
    panel: ProductFacts,
    landing: THREE.Vector3,
    ctx: SnapContext
) => {
    const panelBBox = bboxAt(panel, landing);

    return ctx.productsRef.some(
        (product) =>
            (product.userData.isFloorProduct ||
                product.userData.isKickerLengthProduct) &&
            product.userData.id !== panel.group.userData.id &&
            panelBBox.intersectsBox(
                ctx.products.get(product).bbox.clone().expandByScalar(-1)
            )
    );
};

// Animated doors and drawer fronts are tagged by the animation handler
// (Preview3DCommon/animations/handler.ts); they stand proud of the carcase.
const isDoorOrDrawerPart = (node: THREE.Object3D, root: THREE.Object3D) => {
    let current: THREE.Object3D | null = node;

    while (current && current !== root) {
        if (current.userData.boundsExcludeAnimatedDoorOrDrawerPart === true)
            return true;
        current = current.parent;
    }

    return false;
};

interface Rect {
    out: readonly [number, number];
    front: readonly [number, number];
}

/**
 * The inside corner of an L-shaped corner base, from its carcase components
 * (legs, doors and drawer fronts left out), in the cabinet's own frame.
 * Face A is the front of the leg that runs across; face B is the notch face
 * of the leg that runs front to back. Both are given as planes: face A along
 * `front`, face B along `out`, which points out of the notch.
 *
 * The shared `depth` is how far both panels' outer faces sit behind their
 * faces: the shallower of the two leg-clear setbacks (each puts a panel's
 * back flush on the front-most leg behind that face), so neither panel
 * passes through a leg; KICK_PANEL_SETBACK_MM without legs.
 * @param {ProductFacts} cabinet The L-shaped corner base.
 * @param {number} thickness The kick-length panel's thickness.
 * @return {object|null} The frame, faces and depth, or null when no notch
 * is found.
 */
export const getLShapeCorner = (cabinet: ProductFacts, thickness: number) => {
    const {group} = cabinet;
    const front = frontOf(group);
    const across = new THREE.Vector3(front.z, 0, -front.x);
    const {legBBoxes} = cabinet;
    const carcase: THREE.Box3[] = [];

    group.updateWorldMatrix(true, true);
    group.traverse((child) => {
        if (!(child as THREE.Mesh).isMesh || isAdjustableLeg(child)) return;
        if (isDoorOrDrawerPart(child, group)) return;
        carcase.push(cabinet.componentBBox(child));
    });
    if (!carcase.length) return null;

    const rectsFor = (out: THREE.Vector3): Rect[] =>
        carcase.map((bbox) => ({
            out: extentAlong(bbox, out),
            front: extentAlong(bbox, front),
        }));
    const covers = (rects: Rect[], o: number, f: number) =>
        rects.some(
            (rect) =>
                rect.out[0] <= o &&
                o <= rect.out[1] &&
                rect.front[0] <= f &&
                f <= rect.front[1]
        );
    const probe = 0.5;
    const acrossRects = rectsFor(across);
    const frontEdge = Math.max(...acrossRects.map((rect) => rect.front[1]));
    const acrossEnd = Math.max(...acrossRects.map((rect) => rect.out[1]));
    const acrossStart = Math.min(...acrossRects.map((rect) => rect.out[0]));
    let out: THREE.Vector3;

    if (!covers(acrossRects, acrossEnd - probe, frontEdge - probe)) {
        out = across;
    } else if (!covers(acrossRects, acrossStart + probe, frontEdge - probe)) {
        out = across.clone().negate();
    } else {
        return null;
    }

    const rects = rectsFor(out);
    const notchEdge = Math.max(...rects.map((rect) => rect.out[1]));
    const frontRow = rects.filter(
        (rect) =>
            rect.front[0] <= frontEdge - probe &&
            frontEdge - probe <= rect.front[1]
    );
    const notchColumn = rects.filter(
        (rect) =>
            rect.out[0] <= notchEdge - probe && notchEdge - probe <= rect.out[1]
    );

    if (!frontRow.length || !notchColumn.length) return null;

    const faceB = Math.max(...frontRow.map((rect) => rect.out[1]));
    const faceA = Math.max(...notchColumn.map((rect) => rect.front[1]));

    if (faceB >= notchEdge || faceA >= frontEdge) return null;

    const legFrontA = legBBoxes
        .filter(
            (leg) =>
                extentAlong(leg, out)[1] > faceB &&
                extentAlong(leg, out)[0] < notchEdge &&
                extentAlong(leg, front)[1] <= faceA
        )
        .map((leg) => extentAlong(leg, front)[1]);
    const legFrontB = legBBoxes
        .filter(
            (leg) =>
                extentAlong(leg, front)[1] > faceA &&
                extentAlong(leg, front)[0] < frontEdge &&
                extentAlong(leg, out)[1] <= faceB
        )
        .map((leg) => extentAlong(leg, out)[1]);
    const setbacks = [
        ...(legFrontA.length
            ? [faceA - Math.max(...legFrontA) - thickness]
            : []),
        ...(legFrontB.length
            ? [faceB - Math.max(...legFrontB) - thickness]
            : []),
    ];
    const depth = setbacks.length
        ? Math.min(...setbacks)
        : KICK_PANEL_SETBACK_MM * SCENE_UNITS_PER_MM;

    return {front, out, faceA, faceB, depth};
};

/**
 * Where a kick-length panel running along either face of an L-shaped corner
 * base rests in its inside corner: outer face `depth` behind its face, and
 * its corner end on the line where the other face's panel would have its
 * back. A panel along face A and one along face B share that box minimum and
 * differ only in which way they run.
 * @param {ProductFacts} panel The kick-length panel.
 * @param {ProductFacts} cabinet The L-shaped corner base.
 * @param {SnapContext} ctx The calculation, for every product in the room.
 * @return {THREE.Vector3|null} The panel's resting origin, or null when the
 * L has no notch or a floor product already fills the corner.
 */
export const getLShapeKickPanelLanding = (
    panel: ProductFacts,
    cabinet: ProductFacts,
    ctx: SnapContext
) => {
    const {thickness} = panelExtents(panel);
    const corner = getLShapeCorner(cabinet, thickness);

    if (!corner) return null;

    const {front, out, faceA, faceB, depth} = corner;
    const landing = landingForBBoxMin(
        panel,
        front,
        faceA - depth - thickness,
        out,
        faceB - depth - thickness
    );

    return landingMeetsFloorProduct(panel, landing, ctx) ? null : landing;
};

/**
 * Where a kick-length panel rests at a right angle to the end of another
 * kick-length panel. Either joint closes the corner:
 * - it butts against the other panel: its back on the other panel's end
 *   plane, its thickness over that end, its end on the other panel's face,
 *   and its body running away from it. That is the corner an L-shaped base
 *   produces. One candidate for each end and each side of the other panel.
 * - the other panel butts against it: its side on the other panel's end
 *   plane, just past that end, with its own start or end lined up with the
 *   other panel's face so it covers the other panel's end. One candidate for
 *   each end of the other panel and each end of this one.
 * The candidate nearest the drag target wins.
 * @param {ProductFacts} panel The kick-length panel being moved.
 * @param {ProductFacts} other The kick-length panel already placed.
 * @param {SnapContext} ctx The calculation: the drag target, and every
 * product in the room.
 * @return {THREE.Vector3|null} The panel's resting origin, or null when the
 * panels are parallel or every candidate is occupied.
 */
export const getKickPanelJointLanding = (
    panel: ProductFacts,
    other: ProductFacts,
    ctx: SnapContext
) => {
    const {targetPos} = ctx;
    const placed = panelExtents(other);
    const moving = panelExtents(panel);

    if (placed.runsAlongX === moving.runsAlongX) return null;

    const along = placed.runsAlongX
        ? new THREE.Vector3(1, 0, 0)
        : new THREE.Vector3(0, 0, 1);
    const side = placed.runsAlongX
        ? new THREE.Vector3(0, 0, 1)
        : new THREE.Vector3(1, 0, 0);
    const otherBBox = other.bbox;
    const [start, end] = extentAlong(otherBBox, along);
    const [near, far] = extentAlong(otherBBox, side);
    const [panelStart, panelEnd] = extentAlong(panel.bbox, side);
    const length = panelEnd - panelStart;

    const buttingOther = [start, end - moving.thickness].flatMap((minAlong) =>
        [far, near - length].map((minSide) =>
            landingForBBoxMin(panel, along, minAlong, side, minSide)
        )
    );
    const runningPastEnd = [start - moving.thickness, end].flatMap((minAlong) =>
        [near, far - length].map((minSide) =>
            landingForBBoxMin(panel, along, minAlong, side, minSide)
        )
    );
    const candidates = [...buttingOther, ...runningPastEnd]
        .filter((landing) => !landingMeetsFloorProduct(panel, landing, ctx))
        .sort(
            (a, b) => planDistance(a, targetPos) - planDistance(b, targetPos)
        );

    return candidates[0] ?? null;
};

// A landing on the floor as a candidate: the panel keeps its own height, and
// the landing counts only within the snap distance, measured in plan.
const floorCandidate = (
    landing: THREE.Vector3 | null,
    targetPos: THREE.Vector3
): SnapCandidate | null => {
    if (landing === null) return null;

    const distance = planDistance(landing, targetPos);
    if (distance > DEFAULT_SNAPPING_DISTANCE) return null;

    return {
        position: new THREE.Vector3(landing.x, targetPos.y, landing.z),
        distance,
    };
};

// A kick-length panel at a right angle to the end of another one.
export const getKickPanelJointSnap: SnapStrategy = (ctx, pair) =>
    floorCandidate(
        getKickPanelJointLanding(pair.selected, pair.other, ctx),
        ctx.targetPos
    );

// A kick-length panel into the inside corner of an L-shaped corner base.
export const getLShapeKickPanelSnap: SnapStrategy = (ctx, pair) =>
    floorCandidate(
        getLShapeKickPanelLanding(pair.selected, pair.other, ctx),
        ctx.targetPos
    );
